import { useEffect, useRef } from 'react';

/**
 * A drawn scrollbar — track and thumb, both ordinary divs — sitting ABOVE a
 * horizontally-scrolling element, driven by and driving that element.
 *
 * WHY IT IS DRAWN RATHER THAN STYLED
 * ----------------------------------
 * Two dead ends came first, and both are worth remembering.
 *
 * 1. `transform: rotateX(180deg)` on the scroll container, with the grid
 *    flipped back inside it, so the browser's own bottom scrollbar lands at
 *    the visual top. Fine on desktop Chrome, fatal on phones: a transformed
 *    ancestor becomes the containing block for `position: sticky`
 *    descendants, so mobile WebKit/Blink unpinned the grid's time-label
 *    column, and the 3D rendering context rotateX opens made iOS Safari sort
 *    layers by 3D position and paint those labels behind the cells.
 *
 * 2. A second, empty scroller above the grid with the whole
 *    `::-webkit-scrollbar` cosmetics suite on it. Correct on desktop and
 *    invisible on phones — iOS and Android draw overlay scrollbars that fade
 *    out when idle and ignore ::-webkit-scrollbar sizing entirely, so the
 *    one hint that the grid scrolls sideways was missing on exactly the
 *    devices that need it most.
 *
 * Hence: no native scrollbar anywhere in this feature. The container's own
 * bar is hidden in CSS, and what the user sees is a track div with a thumb
 * div inside it, positioned from scrollLeft and draggable back the other
 * way. It renders identically on iOS, Android and desktop because nothing
 * about it is a browser scrollbar.
 *
 * The thumb is moved with translateX rather than `left` so dragging doesn't
 * relayout. That transform is safe here — the thumb is not an ancestor of
 * anything sticky, which is the whole reason dead end 1 failed.
 *
 * Pointer Events are used for the drag (Safari 13+, every Android browser).
 * Where they're missing the bar still renders and still tracks the grid; it
 * just isn't draggable, and the grid can be swiped directly as always.
 *
 * @param {Array} deps - Values that change the grid's width (court count,
 *   row count, selected sport...). Re-measures when they change; a
 *   ResizeObserver covers everything else.
 * @returns {{trackRef: Object, thumbRef: Object, scrollerRef: Object}}
 */
export default function useTopScrollbar(deps = []) {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    const scroller = scrollerRef.current;
    if (!track || !thumb || !scroller) return undefined;

    // Below this the thumb stops being a grabbable target. On a day-long
    // grid with a dozen courts the proportional width would otherwise come
    // out at a few pixels.
    const MIN_THUMB = 32;
    const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

    // Everything the drag maths needs, read fresh each time: the grid's
    // width changes with the sport tab, the court list and the viewport.
    const metrics = () => {
      const overflow = scroller.scrollWidth - scroller.clientWidth;
      const trackWidth = track.clientWidth;
      const proportional = trackWidth * (scroller.clientWidth / scroller.scrollWidth || 1);
      const thumbWidth = clamp(Math.round(proportional), MIN_THUMB, trackWidth);
      return { overflow, trackWidth, thumbWidth, travel: trackWidth - thumbWidth };
    };

    let frame = 0;
    const render = () => {
      frame = 0;
      const { overflow, thumbWidth, travel } = metrics();
      // A scrollbar over content that already fits is a dead grey stripe.
      // The 1px absorbs sub-pixel widths, which are routine once the court
      // columns are fractional.
      if (overflow <= 1) {
        track.style.display = 'none';
        return;
      }
      track.style.display = '';
      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${travel <= 0 ? 0 : (scroller.scrollLeft / overflow) * travel}px)`;
    };
    // scroll fires far faster than the screen refreshes; coalesce to a frame.
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(render); };

    // ---- drag / jump -----------------------------------------------------
    // null when idle, otherwise how far into the thumb the finger landed.
    let grabOffset = null;
    // A data attribute rather than a class, so the CSS hook is the same
    // string on both grids regardless of their ats-/tss- prefixes.
    const setDragging = (on) => {
      if (on) track.dataset.dragging = 'true';
      else delete track.dataset.dragging;
    };

    const scrollTo = (pointerX) => {
      const { overflow, travel } = metrics();
      if (travel <= 0) return;
      const x = pointerX - track.getBoundingClientRect().left - grabOffset;
      scroller.scrollLeft = clamp(x / travel, 0, 1) * overflow;
    };

    const onPointerMove = (e) => { if (grabOffset !== null) scrollTo(e.clientX); };

    const onPointerUp = () => {
      grabOffset = null;
      setDragging(false);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };

    const onPointerDown = (e) => {
      const { overflow, thumbWidth, travel } = metrics();
      if (overflow <= 0 || travel <= 0) return;
      const trackLeft = track.getBoundingClientRect().left;
      const thumbLeft = thumb.getBoundingClientRect().left - trackLeft;
      const at = e.clientX - trackLeft;
      // Grabbing the thumb keeps it under the same part of the finger;
      // pressing the bare track jumps the thumb to centre on that point,
      // which is what every native scrollbar does.
      grabOffset = (at >= thumbLeft && at <= thumbLeft + thumbWidth)
        ? at - thumbLeft
        : thumbWidth / 2;
      if (grabOffset === thumbWidth / 2) scrollTo(e.clientX);
      setDragging(true);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
      // Stops the press turning into a page scroll or a text selection.
      // `touch-action: none` on the track is the other half of this.
      e.preventDefault();
    };

    const supportsPointer = typeof window.PointerEvent === 'function';
    if (supportsPointer) track.addEventListener('pointerdown', onPointerDown);

    render();
    scroller.addEventListener('scroll', schedule, { passive: true });

    // Re-measure a few times shortly after mount.
    //
    // The bar hides itself when the grid doesn't overflow, and that decision
    // is only as good as the widths available the moment it is made. The
    // guest grid in particular sits close to the line — its columns are
    // narrow enough that a club fits four or five courts on a phone — so a
    // measurement taken before the web fonts swap in, or before the sport's
    // court list settles, can read "fits" and never be revisited: a
    // ResizeObserver only fires when a BOX changes, and scrollWidth can grow
    // without the container's box changing at all.
    //
    // Two frames covers layout settling, document.fonts covers Archivo/Inter
    // arriving late, and the timeout is the backstop for anything else that
    // lands in the first moment. All cheap, all idempotent.
    const ticks = [];
    ticks.push(window.requestAnimationFrame(() => {
      ticks.push(window.requestAnimationFrame(render));
    }));
    const timer = window.setTimeout(render, 400);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(render).catch(() => {});
    }
    // iOS reports the new size a beat after this fires, hence the delay.
    const onOrientation = () => window.setTimeout(render, 300);
    window.addEventListener('orientationchange', onOrientation);

    // The grid's width changes for reasons React never re-renders for: a
    // font finishing loading, an orientation change, a court name wrapping.
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(schedule);
      observer.observe(scroller);
      if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    }
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', onOrientation);
      ticks.forEach((id) => window.cancelAnimationFrame(id));
      window.clearTimeout(timer);
      if (supportsPointer) track.removeEventListener('pointerdown', onPointerDown);
      onPointerUp();
      if (observer) observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { trackRef, thumbRef, scrollerRef };
}
